import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  useTheme,
  Typography,
  Paper,
  Grid,
  alpha,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  EmailOutlined as EmailOutlinedIcon,
  VpnKeyOutlined as VpnKeyOutlinedIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  DirectionsCar as DirectionsCarIcon,
  BookOnline as BookOnlineIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { adminScrollPageSx } from "../../theme/adminLayout";
import { API_BASE_URL } from "../../config/apiBase";
import { apiFetch } from "../../config/apiFetch";

const Dashboard = () => {
  const theme = useTheme();
  const baseColors = tokens(theme.palette.mode);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    renterCount: 0,
    sellerCount: 0,
    vehicleCount: 0,
    bookingCount: 0,
  });

  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await apiFetch("/admin/dashboard/stats");
        const data = await response.json();
        if (data?.success && data?.stats) {
          const { renterCount, sellerCount, vehicleCount, bookingCount } = data.stats;
          setStats({ renterCount, sellerCount, vehicleCount, bookingCount });
        }
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const quickActions = [
    {
      title: "Change Password",
      description: "Update your security credentials",
      icon: <VpnKeyOutlinedIcon />,
      path: "/changepw",
      color: colors.blueAccent[500],
    },
    {
      title: "Change Email",
      description: "Modify your email address",
      icon: <EmailOutlinedIcon />,
      path: "/changeemail",
      color: colors.blueAccent[500],
    },
  ];

  const statCards = [
    {
      title: "Renters",
      count: stats.renterCount,
      icon: <PeopleIcon />,
      color: colors.blueAccent[500],
    },
    {
      title: "Sellers",
      count: stats.sellerCount,
      icon: <StoreIcon />,
      color: colors.blueAccent[600],
    },
    {
      title: "Vehicles",
      count: stats.vehicleCount,
      icon: <DirectionsCarIcon />,
      color: colors.blueAccent[700],
    },
    {
      title: "Bookings",
      count: stats.bookingCount,
      icon: <BookOnlineIcon />,
      color: colors.blueAccent[800],
    },
  ];

  return (
    <Box sx={adminScrollPageSx}>
      {/* Header Section */}
      <Box
        mb={3}
        p={{ xs: 2, md: 3 }}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <DashboardIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography
            variant="h3"
            fontWeight="bold"
            color="white"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}
          >
            Dashboard
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          Welcome back! Here's an overview of your platform
        </Typography>
      </Box>

      {/* Stats Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: colors.blueAccent[500] }} />
        </Box>
      ) : (
        <Grid container spacing={3} mb={4}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: "#ffffff",
                  boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                  border: `1px solid #e0e0e0`,
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  height: "100%",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: `0 15px 35px rgba(0, 0, 0, 0.1)`,
                  },
                }}
              >
                <Stack
                  direction="column"
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: "100%" }}
                >
                  <Box
                    sx={{
                      backgroundColor: alpha(stat.color, 0.2),
                      borderRadius: "50%",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    {React.cloneElement(stat.icon, {
                      sx: {
                        color: stat.color,
                        fontSize: 32,
                      },
                    })}
                  </Box>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    color={stat.color}
                    textAlign="center"
                  >
                    {stat.count.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="#555555"
                    textAlign="center"
                  >
                    {stat.title}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Quick Actions Section Header */}
      <Typography
        variant="h4"
        fontWeight="bold"
        color={colors.blueAccent[700]}
        mb={2}
        pl={1}
      >
        Quick Actions
      </Typography>

      {/* Quick Actions Grid */}
      <Grid container spacing={3}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Link to={action.path} style={{ textDecoration: "none" }}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: "#ffffff",
                  boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                  border: `1px solid #e0e0e0`,
                  borderRadius: 2,
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    backgroundColor: "#ffffff",
                    cursor: "pointer",
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      backgroundColor: alpha(action.color, 0.2),
                      borderRadius: "8px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(action.icon, {
                      sx: {
                        color: action.color,
                        fontSize: 24,
                      },
                    })}
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color="#333333"
                      mb={0.5}
                    >
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="#666666">
                      {action.description}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Link>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;